import { useEffect, useRef, useState } from 'react'
import type { AuthRole } from '@ideon-api/sdk-internal'
import {
  Alert,
  Button,
  CopyButton,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
  Label,
  SecretPanel,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@ideon-api/ideon-ui'
import { parsedSdkErrorBody, sdkErrorMessage } from '../../lib/sdk-client'
import { isForbidden, useCreateMachineUser, type CreateResult } from '../../lib/use-service-accounts'

interface CreateMachineUserDialogProps {
  isOpen: boolean
  onClose: () => void
  slug: string
  onCreated: () => void
}

// Exactly the three values ServiceAccount's role enum defines, and a DB check
// constraint enforces. Typed as AuthRole (a literal union), so an invented or
// renamed value fails tsc rather than needing its own test.
const ROLES: AuthRole[] = ['viewer', 'admin', 'super_admin']

// ServiceAccounts::ProvisionOp::Input sends one bare 403 (platform Forbidden InputRejected) for a non-`ideon`
// principal and for a role above the caller's own, so this copy must not claim which fired.
const FORBIDDEN_CREATE_MESSAGE =
  'That request was refused. Machine users are Ideon-internal, and a role above your own is ' +
  'also refused — ask a super_admin if you need this role.'

// A 503 body is `upstream_unavailable`, which tells an operator nothing, so the dialog
// substitutes its own copy.
const UPSTREAM_MESSAGE =
  'Auth0 rejected the request, so no machine user was created. Nothing was changed — retry, and check the auth service logs if it keeps failing.'

export function CreateMachineUserDialog({
  isOpen,
  onClose,
  slug,
  onCreated,
}: CreateMachineUserDialogProps) {
  const createMachineUser = useCreateMachineUser(slug)
  const [name, setName] = useState('')
  const [role, setRole] = useState<AuthRole>('viewer')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<CreateResult | null>(null)
  // Checked synchronously, so a double-click can't mint two Auth0 applications in
  // the window before `submitting` re-renders (BindAccountDialog's precedent).
  const submittingRef = useRef(false)

  // One dialog instance is reused per account, so reopening must start clean —
  // and must never carry a previous secret into a new session.
  useEffect(() => {
    if (!isOpen) return
    setName('')
    setRole('viewer')
    setError(null)
    setResult(null)
    submittingRef.current = false
  }, [isOpen])

  const secret = result?.created ? result.secret : null

  const handleOpenChange = (open: boolean) => {
    if (open || submitting) return
    // While a secret is on screen, an outside click or Escape must not discard it —
    // it is the only copy that will ever exist. Dismissal goes through the panel's
    // own acknowledge button instead.
    if (secret) return
    onClose()
  }

  const handleSubmit = async () => {
    if (submittingRef.current || !name.trim()) return
    submittingRef.current = true
    setSubmitting(true)
    setError(null)
    try {
      const created = await createMachineUser(name.trim(), role)
      setResult(created)
      // The list refreshes either way: an idempotent hit means the key exists and
      // the operator should see it, even though no secret came back.
      onCreated()
    } catch (e: unknown) {
      setError(messageFor(e))
    } finally {
      submittingRef.current = false
      setSubmitting(false)
    }
  }

  const messageFor = (e: unknown): string => {
    if (isForbidden(e)) return FORBIDDEN_CREATE_MESSAGE
    if (parsedSdkErrorBody(e).status === 503) return UPSTREAM_MESSAGE
    return sdkErrorMessage(e, 'Could not create the machine user.')
  }

  const handleAcknowledge = () => {
    setResult(null)
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create machine user</DialogTitle>
        </DialogHeader>

        {error && <Alert variant="error">{error}</Alert>}

        {secret && (
          <div className="flex flex-col gap-3">
            {/* Both halves, in the order they get pasted into config. The client id is
                public, so it renders plainly -- masking an identifier would be theatre
                and would slow down the one moment both values are available. */}
            {result?.clientId && (
              <div className="flex flex-col gap-1.5">
                <span className="font-inter text-xs font-medium text-ideon-gray-70 dark:text-ideon-gray-60">
                  Client ID
                </span>
                <div className="flex items-center gap-2">
                  <code
                    data-testid="client-id-value"
                    className="flex-1 truncate rounded-[.375rem] border border-ideon-gray-40 bg-white px-3 py-2 font-mono text-sm text-ideon-gray-black dark:border-ideon-gray-80 dark:bg-ideon-gray-95 dark:text-ideon-gray-30"
                  >
                    {result.clientId}
                  </code>
                  <CopyButton value={result.clientId} label="Copy client id" iconOnly />
                </div>
              </div>
            )}
            <SecretPanel secret={secret} label="Client secret" onAcknowledge={handleAcknowledge} />
          </div>
        )}

        {/* An idempotent hit returns no secret. Say so plainly: an empty or
            placeholder secret panel reads either as a bug or as a lost
            credential, and both are wrong. */}
        {result && !result.created && (
          <Alert variant="info">
            A machine user named <strong>{result.name}</strong> already exists on this account.
            Its client secret cannot be retrieved again — revoke it and create a new one if you
            need fresh credentials.
          </Alert>
        )}

        {!result && (
          <div className="flex flex-col gap-4">
            <FormField label="Name" htmlFor="machine-user-name">
              <Input
                id="machine-user-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="acme-prod"
                autoComplete="off"
                spellCheck={false}
                disabled={submitting}
              />
            </FormField>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="machine-user-role">Role</Label>
              <Select value={role} onValueChange={(v) => setRole(v as AuthRole)}>
                <SelectTrigger id="machine-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (
                    <SelectItem key={r} value={r}>
                      {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {role === 'super_admin' && (
                <p className="text-xs text-ideon-state-warning-dark">
                  super_admin grants this key full access to the account. Pick viewer unless
                  the integration genuinely writes.
                </p>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          {!result && (
            <>
              <Button variant="text" onClick={onClose} disabled={submitting}>
                Cancel
              </Button>
              <Button variant="filled" onClick={handleSubmit} disabled={submitting || !name.trim()}>
                {submitting ? 'Creating…' : 'Create'}
              </Button>
            </>
          )}
          {result && !secret && (
            <Button variant="filled" onClick={handleAcknowledge}>
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
